// src/pages/Sotuv.js
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./Sotuv.css";
import api from "../api";
import { getWarehouseAccess } from "../auth";
import { adminPath } from "../routes";
import { useSklad } from "../SkladContext";
import {
  getReceiptPrintExtras,
  openReceiptWindow as openSharedReceiptWindow,
} from "../receiptPrinting";

/* ================== Config ================== */
const PAGE_SIZE = 36;
const SEARCH_DEBOUNCE_MS = 250;

/* ================== Decimal helpers ================== */
const MONEY_QUANT = 1000; // 0.001 aniqlik
const normDecStr = (v) => {
  if (v == null) return "";
  let s = String(v).trim().replace(",", ".");
  s = s.replace(/[^\d.-]/g, "");
  if (s.includes("-")) s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const i = s.indexOf(".");
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (["", "-", ".", "-."].includes(s)) return "";
  return s;
};
const toNum = (v) => {
  const n = Number(normDecStr(v));
  return Number.isFinite(n) ? n : 0;
};
const toFixed3Str = (v) => {
  const n = Math.round(toNum(v) * MONEY_QUANT);
  const neg = n < 0;
  const abs = Math.abs(n);
  const ip = String(Math.floor(abs / MONEY_QUANT));
  const fp = String(abs % MONEY_QUANT).padStart(3, "0");
  return (neg ? "-" : "") + ip + "." + fp;
};
// >>> BU YERGA QO'YING <<<
const toSomInt = (v) => {
  const s = String(v ?? "").replace(/[^\d]/g, "");
  return s ? Math.max(0, parseInt(s, 10)) : 0;
};
const toSomRounded = (v) => Math.max(0, Math.round(toNum(v)));
/* ================== Sana helpers ================== */
const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/* ================== Formatters ================== */
const fmtUSD = (v, { min = 2, max = 4 } = {}) =>
  toNum(v).toLocaleString("uz-UZ", { minimumFractionDigits: min, maximumFractionDigits: max }) + " so'm";
const fmtUZS = (v) => Math.round(toNum(v)).toLocaleString("uz-UZ") + " so'm";


// --- STOCK helpers ---
// Avval yuklangan mahsulotlardan, bo'lmasa savat qatoridagi saqlangan qoldiqdan
const stockByProductId = (products, id, cart = []) => {
  const p = products.find((pp) => pp.id === id);
  if (p) return stockOf(p);
  const c = cart.find((cc) => cc.id === id);
  return c?.stock ?? null;
};
//   helper
const clampQtyByStock = (qty, maxStk) => {
  const q = Math.max(1, Number(qty) || 1);
  if (maxStk == null || maxStk <= 0) return q;  // stokni bilmasak – cheklamaymiz
  return Math.min(q, maxStk);
};

/* ================== Helpers ================== */
const stockOf = (p) => Number(p?.soni ?? p?.stock ?? 0);
const asArr = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.results || []);
const uniqById = (list) => {
  const seen = new Set(); const out = [];
  for (const it of list) { const k = String(it.id); if (!seen.has(k)) { seen.add(k); out.push(it); } }
  return out;
};

/* ================== Status utils ================== */
const normalizeBool = (v) =>
  v === true || v === 1 || v === "1" || (typeof v === "string" && v.toLowerCase() === "true");
const normalizeDate = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : toLocalYMD(d);
};
const UI_STATES = { OVERDUE: "overdue_debtor", NOT_DUE: "debtor_not_due", NEUTRAL: "neutral", CREDITOR: "creditor" };
const SERVER2UI_STATUS = (s) => {
  switch ((s || "").toLowerCase()) {
    case "overdue_debtor": return UI_STATES.OVERDUE;
    case "debtor_not_due": return UI_STATES.NOT_DUE;
    case "neutral":        return UI_STATES.NEUTRAL;
    case "creditor":       return UI_STATES.CREDITOR;
    default:               return null;
  }
};
const computeUiStatus = ({ signedDebt, hasOverdue, dueDate }) => {
  const bal = toNum(signedDebt || 0);
  const due = normalizeDate(dueDate);
  if (bal < 0) {
    if (normalizeBool(hasOverdue)) return UI_STATES.OVERDUE;
    if (due && due < toLocalYMD(new Date())) return UI_STATES.OVERDUE;
    return UI_STATES.NOT_DUE;
  }
  if (bal > 0) return UI_STATES.CREDITOR;
  return UI_STATES.NEUTRAL;
};
const badgeClass = (ui) =>
  ui === UI_STATES.OVERDUE ? "badge-red" :
  ui === UI_STATES.NOT_DUE ? "badge-yellow" :
  ui === UI_STATES.CREDITOR ? "badge-green" : "";

/* ================== Narx helpers ================== */

// Backenddan kelgan narxlar roʻyxatini (label, price) ga normallashtirish
const priceMapForProduct = (p) => {
  if (!p) return [];

  // 1) Yangi backend: p.narxlar[]
  if (Array.isArray(p.narxlar) && p.narxlar.length) {
    return p.narxlar
      .map((n) => {
        const label = String(
          n.sarxturi ??
            n.narxturi ??
            n.narx_turi?.nom ??
            n.narx_turi?.name ??
            n.narx_turi_name ??
            n.sarx_turi ??
            n.label ??
            n.nom ??
            n.name ??
            n?.price_type?.name ??
            n?.type?.name ??
            ""
        ).trim();

        const price = toNum(
          n.narx ?? n.narxi ?? n.price ?? n.usd ?? n.price_usd ?? 0
        );

        return label ? { label, price } : null;
      })
      .filter(Boolean);
  }

  // 2) Eski backend: mahsulot obyektidagi toʻgʻridan-toʻgʻri maydonlar
  const legacy = [];
  const take = (lbl, ...cands) => {
    for (const k of cands) {
      if (p[k] != null && p[k] !== "") {
        legacy.push({ label: lbl, price: toNum(p[k]) });
        return;
      }
    }
  };
  take("donaga", "donaga", "donaga_narx", "narx_donaga", "narx_dollar", "narx");
  take("optom", "optom", "optom_narx", "narx_optom");
  take("vip", "vip", "vip_narx", "narx_vip");

  return legacy;
};

// Tanlangan label (yoki id) bo‘yicha USD narxni olish
const priceByTypeUSD = (p, labelOrId, priceTypes) => {
  const all = priceMapForProduct(p);
  if (!all.length) return null;

  // id -> nom
  let label = "";
  const byId = priceTypes.find((t) => String(t.id) === String(labelOrId));
  if (byId) label = byId.nom || byId.name || "";
  if (!label) label = String(labelOrId || "").trim();

  const lab = label.toLowerCase();
  const hit = all.find((x) => String(x.label || "").toLowerCase() === lab);
  return hit ? hit.price : null;
};


const computeDiscountValue = (mode, discount, subtotal) => {
  const sub = toNum(subtotal);
  if (mode === "percent") {
    const pct = Math.min(100, Math.max(toNum(discount), 0));
    return (pct * sub) / 100;
  }
  return Math.min(Math.max(toNum(discount), 0), sub);
};
const firstSkladIdFromCart = (cart, products) => {
  for (const c of cart) {
    if (c?.sklad) return c.sklad;                // avval cartdan
    const p = products.find((pp) => pp.id === c.id);
    if (p?.sklad) return p.sklad;                // keyin fallback
  }
  return null;
};
const buildReceiptPayload = ({ cart, seller, payType, discountValue, products, note }) => {
  const items = cart.map((it) => ({
    product_name: it.name || "Noma’lum tovar",
    qty: Number(it.qty || 0),
    price: toFixed3Str(it.unitPrice),
    product_id: it.id || null,
    series: it.sku || null,
  }));
  return {
    buyer_name: seller?.name || "",
    buyer_phone: seller?.phone || "",
    buyer_location: "",
    pay_type: payType, // "naqd" | "nasiya" | "karta"
    seller: seller?.id || null,
    sklad: firstSkladIdFromCart(cart, products),
    discount: toFixed3Str(discountValue || 0),
    note: note || "",          // ✅ endi argumentdan keladi
    items,
  };
};


/* ================== Mijoz rasmi ================== */
const customerImg = (s) =>
  s?.avatar ||
  s?.mijoz_rasmi_url ||
  (s?.mijoz_rasmi && s.mijoz_rasmi) ||
  (s?.mijozrasminomi && `/media/${s.mijozrasminomi}`) ||
  "https://i.pravatar.cc/80?img=1";

const mapSellerRecord = (s) => {
  const signedDebt = toNum(s?.qarhaq || s?.signedDebt || 0);
  const due = normalizeDate(s?.next_due_date || s?.dueDate || "");
  const hasOver = normalizeBool(s?.has_overdue);
  const srv = SERVER2UI_STATUS(s?.current_status);
  const uiState = srv || computeUiStatus({ signedDebt, hasOverdue: hasOver, dueDate: due });

  return {
    id: s?.id,
    name: s?.ismi || s?.name || "",
    phone: s?.telraqam1 || s?.telraqam2 || s?.phone || "",
    avatar: customerImg(s),
    debtAbs: Math.abs(signedDebt),
    signedDebt,
    uiState,
    tariff: s?.mijoztarifi || s?.tariff || "",
    current_status: s?.current_status || "",
    next_due_date: due,
    has_overdue: hasOver,
  };
};

/* ================== Product Card ================== */
const ProductCard = React.memo(function ProductCard({
  product, onAdd, usdPrice, outOfStock, allowSell, selected, wrongSklad
}) {
  const img = product.rasm_url || (product.rasm ? product.rasm : null);
  const handleAdd = () => { if (!outOfStock && allowSell) onAdd(product, usdPrice ?? 0); };
  const priceLabel =
    product?.can_view_prices === false
      ? "Narx yopiq"
      : usdPrice != null
      ? fmtUSD(usdPrice)
      : "-";

  return (
    <div
      className={[
        "p-card",
        allowSell ? "clickable" : "disabled",
        outOfStock ? "oos" : "",
        wrongSklad ? "wrong-sklad" : "",
        selected ? "selected" : "",
      ].join(" ").trim()}
      onClick={handleAdd}
      title={outOfStock
        ? "Stok yo’q"
        : wrongSklad
        ? "Bu mahsulot boshqa sklad uchun"
        : !allowSell
        ? "Sizda bu sklad uchun savdo ruxsati yo’q"
        : "Savatchaga qo’shish"}
    >
      {outOfStock && <span className="oos-tag">Stok yo‘q</span>}

      <div className="img-ph">
        {img ? (
          <img
            className="p-img"
            src={img}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; e.target.parentNode.classList.add("no-img-fallback"); }}
          />
        ) : (
          <div className="no-img" />
        )}
      </div>

      <div className="p-meta">
        <div className="p-name">{product.nom || product.name}</div>
        <div className="p-sku">{product.seriyasi || product.sku}</div>
        <div className="p-stock">{stockOf(product)} dona</div>
        <div className="p-price">{priceLabel}</div>
      </div>
    </div>
  );
});

/* ================== Cart Row ================== */
const CartRow = React.memo(function CartRow({
  item, onQtyAbs, onRemove, onUnitChange, onLineChange
}) {
  const [unitStr, setUnitStr] = useState(String(item.unitPrice));
  const [lineStr, setLineStr] = useState(String(toNum(item.unitPrice) * item.qty));
  const [qtyStr, setQtyStr] = useState(String(item.qty));

  useEffect(() => {
    setUnitStr(String(item.unitPrice));
    setLineStr(String(toNum(item.unitPrice) * item.qty));
    setQtyStr(String(item.qty));
  }, [item.unitPrice, item.qty]);

  return (
    <div className="cart-row">
      <div className="cart-title">
        <div className="c-name">{item.name}</div>
        <div className="c-sku">{item.sku}</div>
      </div>

      <div className="cart-qty">
        <button onClick={() => onQtyAbs(item.id, Math.max(1, item.qty - 1))} disabled={item.qty <= 1}>–</button>
        <input
          className="in-num qty-in"
          type="text"
          inputMode="numeric"
          value={qtyStr}
          onChange={(e) => {
            const n = Math.max(1, parseInt(e.target.value.replace(/[^\d]/g, "") || "1", 10));
            setQtyStr(String(n));
          }}
          onBlur={() => onQtyAbs(item.id, Math.max(1, parseInt(qtyStr || "1", 10)))}
        />
        <button onClick={() => onQtyAbs(item.id, item.qty + 1)}>+</button>
      </div>

      <div className="cart-unit editable">
        <input
          className="in-num"
          type="text"
          inputMode="decimal"
          value={unitStr}
          onChange={(e) => setUnitStr(normDecStr(e.target.value))}
          onBlur={() => onUnitChange(item.id, unitStr)}
        />
        <small>so'm</small>
      </div>

      <div className="cart-sum editable">
        <input
          className="in-num"
          type="text"
          inputMode="decimal"
          value={lineStr}
          onChange={(e) => setLineStr(normDecStr(e.target.value))}
          onBlur={() => onLineChange(item.id, lineStr)}
        />
        <small>so'm</small>
      </div>

      <button className="cart-remove" onClick={() => onRemove(item.id)}>×</button>
    </div>
  );
});

/* ================== Receipt printing (80mm) ================== */
const renderReceiptHTML = (receipt, extras) => {
  const {
    payType = "naqd",
    dueDate = null,
    inUsd = 0,
    inUzsCash = 0,
    inUzsCard = 0,
    note = "",
  } = extras || {};

  // --- Raqam format: 3 xona, ortiqcha 0 va nuqtani olib tashlaydi ---
  const f3 = (n) => Number(n || 0).toFixed(3).replace(/\.?0+$/, "");

  /* --- CSS --- */
  const css = `
  @page { size: 80mm auto; margin: 4mm; }

  body {
    font-family: 'DejaVu Sans', Arial, sans-serif;
    font-size: 12px;
    margin: 0;
    padding: 0;
    color: #111;
    -webkit-print-color-adjust: exact;
  }

  /* ====== Bosh qism ====== */
  .brand-block {
    background: #000;
    text-align: center;
    padding: 10px 0;
    margin-bottom: 6px;
  }
  .brand-logo {
    width: 60px;
    height: auto;
    display: inline-block;
  }
  .h {
    text-align: center;
    font-weight: 700;
    font-size: 14px;
    margin: 4px 0 8px;
    letter-spacing: 0.3px;
  }

  /* ====== Metadata ====== */
  .meta {
    font-size: 11px;
    text-align: left;
    margin-bottom: 4px;
    color: #333;
    line-height: 1.3;
  }

  /* ====== Ajratgich ====== */
  .s {
    border-top: 1px dashed #999;
    margin: 6px 0;
  }

  /* ====== Jadval ====== */
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  td {
    padding: 2px 0;
    vertical-align: top;
  }
  td.name {
    width: 68%;
    padding-right: 4px;
    white-space: normal;
    word-break: break-word;
  }
  td.calc {
    width: 32%;
    text-align: right;
    white-space: nowrap;
  }
  .code {
    color: #666;
    font-size: 10px;
    margin-left: 4px;
  }

  /* ====== Summalar ====== */
  .total b {
    font-size: 14px;
  }

  /* ====== Izoh bloki ====== */
  .note-block {
    font-size: 11px;
    color: #222;
    background: #f8f8f8;
    padding: 4px 6px;
    border-left: 3px solid #ccc;
    margin-top: 6px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  /* ====== Tugmalar (faqat ekranda) ====== */
  .btns {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    gap: 6px;
  }
  .btn {
    padding: 6px 10px;
    border: 1px solid #333;
    background: #fff;
    cursor: pointer;
    border-radius: 6px;
    font-size: 12px;
  }

  @media print {
    .btns { display: none; }
    body { background: #fff !important; }
  }
`;
    // --- Izoh (XSSdan tozalab) ---
  const noteStr = String(note || "").trim();
  const esc = (s) => s.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
  const noteHtml = noteStr
    ? `<div class="note-block"><b>Izoh:</b> ${esc(noteStr)}</div>`
    : "";


  // --- Items qatorlari: "NOM  KOD    qty × unit = line" ---
  const items = Array.isArray(receipt.items) ? receipt.items : [];
  const rows = items.map((it) => {
    const rawName = it.product_name || it.name || "Noma'lum";
    const name = rawName.length > 48 ? `<span style="font-size:11px">${rawName}</span>` : rawName;
    const code = it.series || it.sku || it.code || "";
    const qty  = Number(it.qty || it.miqdor || 0);
    const unit = Number(it.unit_price ?? it.price ?? 0); // birlik narx
    const line = qty * unit;

    return `
      <tr>
        <td class="name">${name}${code ? `<span class="code">${code}</span>` : ""}</td>
        <td class="calc">${f3(qty)} × ${f3(unit)} = ${f3(line)}</td>
      </tr>
    `;
  }).join("");

  // --- Sana/vaqt ---
  const dt = new Date(receipt.created_at || Date.now()).toLocaleString("uz-UZ", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });

  // --- Totallar ---
  const subtotal = f3(receipt.subtotal ?? 0);
  const discount = f3(receipt.discount ?? 0);
  const total    = f3(receipt.total ?? 0);
  const buyer    = receipt.buyer_name || "—";

  // --- To‘lov qismi (0 bo‘lsa ko‘rinmaydi) ---
  const lines = [];
  lines.push(`<tr><td class="name">To‘lov turi</td><td class="calc">${payType === "nasiya" ? "Nasiya" : "Naqd"}</td></tr>`);

  if (payType === "nasiya" && dueDate) {
    lines.push(`<tr><td class="name">Muddati</td><td class="calc">${dueDate}</td></tr>`);
  } else {
    if (Number(inUsd) > 0)
      lines.push(`<tr><td class="name">Terminal (so'm)</td><td class="calc">${Math.round(inUsd).toLocaleString("uz-UZ")} so'm</td></tr>`);
    if (Number(inUzsCash) > 0)
      lines.push(`<tr><td class="name">Naqd (so‘m)</td><td class="calc">${Math.round(inUzsCash).toLocaleString("uz-UZ")} so‘m</td></tr>`);
    if (Number(inUzsCard) > 0)
      lines.push(`<tr><td class="name">Kartadan (so‘m)</td><td class="calc">${Math.round(inUzsCard).toLocaleString("uz-UZ")} so‘m</td></tr>`);
  }

  // --- HTML ---
  return `
  <html><head><meta charset="utf-8"><title>Chek</title><style>${css}</style></head>
  <body>
    <div class="brand-block">
      <img src="/logo.jpg" alt="YUtech" class="brand-logo" />
    </div>

    <div class="h">SAVDO CHEK</div>
    <div class="meta">${dt}<br>Haridor: ${buyer}</div>
    <div class="s"></div>

    <table>${rows}</table>

    <div class="s"></div>
    <table>
      <tr><td class="name">Jami</td><td class="calc">${subtotal}</td></tr>
      <tr><td class="name">Chegirma</td><td class="calc">${discount}</td></tr>
      <tr class="total"><td class="name"><b>Yakuniy</b></td><td class="calc"><b>${total}</b></td></tr>
    </table>

    <div class="s"></div>
    <table>${lines.join("")}</table>

    ${noteHtml}

    <div class="btns">
      <button class="btn" onclick="window.print()">Print</button>
      <button class="btn" onclick="window.close()">Yopish</button>
    </div>
  </body></html>`;
};

// eslint-disable-next-line no-unused-vars
const openReceiptWindow = (receipt, extras) => {
  const html = renderReceiptHTML(receipt, extras);
  const win = window.open("", "_blank", "width=400,height=700");
  if (win) {
    win.document.write(html);
    win.document.close();
    return;
  }
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "-10000px";
  iframe.style.bottom = "-10000px";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
};

/* ================== Asosiy sahifa ================== */
export default function SalesPage() {
  const warehouseAccess = useMemo(() => getWarehouseAccess(), []);
  const [mobileTab, setMobileTab] = useState("products");

  /* Qidiruvlar */
  const [qProductsLeft, setQProductsLeft] = useState("");
  // Serverga har harfda emas, yozish to'xtagach so'rov yuboriladi
  const [qProductsDebounced, setQProductsDebounced] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQProductsDebounced(qProductsLeft.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [qProductsLeft]);
  const [qSellers, setQSellers] = useState("");
  const [qCart, setQCart] = useState("");

  /* Filtrlar */
  const { activeSklad } = useSklad();
  const [warehouse, setWarehouse] = useState(activeSklad);
  const [priceType, setPriceType] = useState("");
  // Adminlar uchun global sklad o'zgarganda sync; cashierlar uchun init effektida o'rnatiladi
  useEffect(() => {
    if (warehouseAccess.allAccess) setWarehouse(activeSklad);
  }, [activeSklad, warehouseAccess.allAccess]);
  const [category, setCategory] = useState("all");
  const [brand, setBrand] = useState("all");
  // 🔥 YANGI: Qolgan / Qolmagan / Barchasi filtri
  // "available" – qolgan (stok > 0), "out" – tugagan (stok <= 0), "all" – hammasi
  const [stockFilter, setStockFilter] = useState("available");


  /* Mijoz/Reference */
  const [seller, setSeller] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  /* Mahsulotlar */
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  /* ------- O'ng panel (savatcha) kengligi: chap chetidan sudrab o'zgartiriladi ------- */
  const CART_W_KEY = "sotuv_cart_w";
  const gridRef = useRef(null);
  const [cartW, setCartW] = useState(() => {
    try { const v = parseInt(localStorage.getItem(CART_W_KEY), 10); return v > 0 ? v : null; } catch { return null; }
  });
  const saveCartW = (w) => {
    setCartW(w);
    try { w ? localStorage.setItem(CART_W_KEY, String(w)) : localStorage.removeItem(CART_W_KEY); } catch {}
  };
  const startCartResize = (e) => {
    const grid = gridRef.current;
    if (!grid) return;
    e.preventDefault();
    const rect = grid.getBoundingClientRect();
    const clampW = (x) => Math.round(Math.min(Math.max(rect.right - x, 340), rect.width - 320));
    let last = null;
    const onMove = (ev) => {
      last = clampW(ev.clientX);
      grid.style.setProperty("--cart-w", `${last}px`);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.classList.remove("cart-resizing");
      if (last) saveCartW(last);
    };
    document.body.classList.add("cart-resizing");
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const fetchingRef = useRef(false);
  const [renderLimit, setRenderLimit] = useState(PAGE_SIZE * 2);

  /* Savatcha */
  const [cart, setCart] = useState([]);

  /* To‘lov */
  const [payType, setPayType] = useState("naxt"); // "naxt" | "nasiya" — standart naqd (POS kabi)
  const [termDays, setTermDays] = useState("");


  /* Chegirma/izoh */
  const [discountMode, setDiscountMode] = useState("amount");
  const [discount, setDiscount] = useState("0");
  const [note, setNote] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Oxirgi tasdiqlangan savdo cheki — avtomatik chiqmaydi, "Chek chiqarish" tugmasi bilan chiqadi
  const [lastReceipt, setLastReceipt] = useState(null);


  /* Kassa kirim breakdown */
  const [inUsd, setInUsd] = useState("");
  const [inUzsCash, setInUzsCash] = useState("");
  const [inUzsCard, setInUzsCard] = useState("");
  const [changeMode, setChangeMode] = useState("cash");

  // 💳 To‘lov turi o‘zgarsa — boshqa maydonlarni tozalaymiz
  useEffect(() => {
    if (payType !== "naxt") {
      setInUsd("");
      setInUzsCash("");
      setInUzsCard("");
      setChangeMode("cash");
    }
  }, [payType]);

  /* ------- Init ------- */
  useEffect(() => {
    (async () => {
      try {
        // Mahsulotlarni filtr effekti yuklaydi — bu yerda faqat ma'lumotnomalar
        const [w, t, c, b] = await Promise.all([
          api.get("sklad/"),
          api.get("narx-turi/"),
          api.get("maxsulot-kategoriyasi/"),
          api.get("brend/"),
        ]);
        const W = asArr(w), T = asArr(t), C = asArr(c), B = asArr(b);
        const visibleWarehouses = warehouseAccess.allAccess
          ? W
          : W.filter((item) => warehouseAccess.allowedIds.includes(Number(item.id)));
        setWarehouses([{ id: "all", nom: "Hammasi" }, ...visibleWarehouses]);

        // Cashier uchun: allowed sklad avtomatik tanlansin
        if (!warehouseAccess.allAccess) {
          if (warehouseAccess.allowedIds.length === 1) {
            setWarehouse(String(warehouseAccess.allowedIds[0]));
          } else {
            setWarehouse("all");
          }
        }
        setPriceTypes(T);
        setCategories([{ id: "all", nom: "Barchasi" }, ...C]);
        setBrands([{ id: "all", nom: "Barchasi" }, ...B]);

        // Default narx turi: "donaga" yoki birinchi
        const donaga = T.find((x) => (x.nom || "").toLowerCase() === "donaga");
        setPriceType(String(donaga ? donaga.id : T[0]?.id || ""));
      } catch (e) {
        console.log("Init load error", e?.response?.status, e?.response?.data);
        alert("Ma'lumotlarni yuklashda xatolik yuz berdi.");
      }
    })();
  }, [warehouseAccess.allAccess, warehouseAccess.allowedIds]);

  /* ------- Mijoz qidirish (debounce) ------- */
  const [sellerMatches, setSellerMatches] = useState([]);
  useEffect(() => {
    const q = qSellers.trim();
    if (q.length < 2) { setSellerMatches([]); return; }
    const id = setTimeout(async () => {
      try {
        const res = await api.get(`sotuvchi/?search=${encodeURIComponent(q)}`);
        const arr = (res.data.results || res.data || []).map((s) => mapSellerRecord(s));
        setSellerMatches(arr);
      } catch (e) {
        console.log("Seller search error", e?.response?.status, e?.response?.data);
        setSellerMatches([]);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [qSellers]);

  /* ------- Qadalgan xaridorlar (har bir akkauntning o'zi uchun, serverda saqlanadi) ------- */
  const MAX_PINNED = 4;
  const [pinned, setPinned] = useState([]);
  useEffect(() => {
    api.get("me/pinned-sellers/")
      .then(({ data }) => setPinned((data?.sellers || []).map(mapSellerRecord)))
      .catch((e) => console.log("Pinned load error", e?.response?.status));
  }, []);
  const pinnedIds = useMemo(() => new Set(pinned.map((p) => p.id)), [pinned]);

  const togglePin = async (s) => {
    const ids = pinned.map((p) => p.id);
    const next = ids.includes(s.id) ? ids.filter((id) => id !== s.id) : [...ids, s.id];
    if (next.length > MAX_PINNED) {
      alert(`Ko'pi bilan ${MAX_PINNED} ta xaridorni qadash mumkin. Avval bittasini olib tashlang.`);
      return;
    }
    try {
      const { data } = await api.put("me/pinned-sellers/", { ids: next });
      setPinned((data?.sellers || []).map(mapSellerRecord));
    } catch (e) {
      alert(e?.response?.data?.detail || "Qadashda xato.");
    }
  };

  // Qadalgan xaridor tanlanganda balansi eskirgan bo'lishi mumkin — serverdan yangisini olamiz
  const selectPinned = async (s) => {
    setSeller(s);
    try {
      const { data } = await api.get(`sotuvchi/${s.id}/`);
      const fresh = mapSellerRecord(data);
      setSeller((cur) => (cur?.id === s.id ? fresh : cur));
      setPinned((prev) => prev.map((p) => (p.id === s.id ? fresh : p)));
    } catch {}
  };


  /* ------- Mahsulot so'rovi parametrlari (server sahifalaydi va filtrlaydi) ------- */
  const buildProductParams = useCallback((pageNum) => {
    const params = new URLSearchParams();
    params.set("page", String(pageNum));
    params.set("page_size", String(PAGE_SIZE));
    params.set("view", "sale");
    if (warehouse && warehouse !== "all") params.set("sklad", String(warehouse));
    if (category !== "all") params.set("kategoriya", String(category));
    if (brand !== "all") params.set("brand", String(brand));
    if (stockFilter !== "all") params.set("stock", stockFilter);
    if (qProductsDebounced) params.set("search", qProductsDebounced);
    return params;
  }, [warehouse, category, brand, stockFilter, qProductsDebounced]);

  /* ------- Filtrlar o‘zgarsa – 1-sahifadan ------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(`maxsulot/?${buildProductParams(1).toString()}`);
        const rows = Array.isArray(data) ? data : data?.results || [];
        if (cancelled) return;
        setProducts(uniqById(rows));
        setPage(1);
        setHasMore(Boolean(data?.next));
      } catch (e) {
        console.log("PRODUCTS FIRST ERR", e?.response?.status, e?.response?.data);
        if (!cancelled) { setProducts([]); setHasMore(false); }
      } finally { if (!cancelled) setLoadingPage(false); }
    })();
    return () => { cancelled = true; };
  }, [buildProductParams]);

  useEffect(() => {
    setRenderLimit(PAGE_SIZE * 2);
  }, [buildProductParams]);

  /* ------- Keyingi sahifalarni olish ------- */
  const fetchMore = useCallback(async (nextPage) => {
    const { data } = await api.get(`maxsulot/?${buildProductParams(nextPage).toString()}`);
    const rows = Array.isArray(data) ? data : data?.results || [];
    setProducts((prev) => uniqById([...prev, ...rows]));
    setHasMore(Boolean(data?.next));
  }, [buildProductParams]);

  /* ------- Infinite scroll ------- */
  useEffect(() => {
    if (!loaderRef.current) return;
    const node = loaderRef.current;

    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting) return;
      setRenderLimit((v) => v + PAGE_SIZE);
      if (!hasMore || loadingPage) return;
      if (fetchingRef.current) return;

      fetchingRef.current = true;
      const next = page + 1;
      setPage(next);
      try {
        setLoadingPage(true);
        await fetchMore(next);
      } finally {
        setLoadingPage(false);
        fetchingRef.current = false;
      }
    }, { rootMargin: "400px 0px" });

    io.observe(node);
    return () => io.disconnect();
  }, [loaderRef, page, hasMore, fetchMore, loadingPage]);

  /* ------- Savatcha amallari ------- */
  const addToCart = useCallback((p, unitPrice) => {
  const stk = stockOf(p);
  if (stk <= 0) { alert(`${p.nom} (${p.seriyasi}) omborda yo’q.`); return; }
  if (p?.can_sell === false) return;

  // Tanlangan sklad bo’lsa — boshqa sklad mahsulotini qo’shish mumkin emas
  if (warehouse && warehouse !== "all" && p?.sklad && String(p.sklad) !== String(warehouse)) {
    alert(`Bu mahsulot boshqa skladga tegishli. Iltimos, to’g’ri sklad tanlang.`);
    return;
  }

  setCart((old) => {
    const ex = old.find((x) => x.id === p.id);
    const u = toNum(unitPrice);
    if (ex) {
      if (ex.qty + 1 > stk) {
        alert(`${p.nom} (${p.seriyasi}) yetarli emas. Omborda: ${stk} dona.`);
        return old;
      }
      const upd = old.map((x) =>
        x.id === p.id ? { ...x, qty: x.qty + 1, unitPrice: u, stock: stk } : x
      );
      const found = upd.find((x) => x.id === p.id);
      return [found, ...upd.filter((x) => x.id !== p.id)];
    }
    return [{
      id: p.id,
      name: p.nom,
      sku: p.seriyasi,
      unitPrice: u,
      qty: 1,
      sklad: p?.sklad ?? null,
      // Mahsulot ro'yxati sahifalangani uchun qoldiq savatda ham saqlanadi
      stock: stk,
    }, ...old];
  });
}, [warehouse]);

  /* ------- QR/Barcode scanner (seriya + Enter) ------- */
  // Bir kod bir nechta mahsulotda bo'lsa — kassir ro'yxatdan tanlaydi
  const [scanChoices, setScanChoices] = useState(null); // { code, items }

  const addScanned = useCallback((prod) => {
    const label = resolvePriceLabel(seller, priceType, priceTypes);
    let usd = (prod?.narxlar || []).find(
      (n) => String(n.sarxturi || "").toLowerCase() === label
    )?.narx;
    if (usd == null) {
      usd = (prod?.narxlar || [])[0]?.narx ?? 0;
    }
    addToCart(prod, toNum(usd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller, priceType, priceTypes, addToCart]);

  const handleScanSeries = useCallback(async (rawSeries) => {
    const series = String(rawSeries || "").trim();
    if (!series) return;

    const needle = series.toLowerCase();
    const inSklad = (p) => !warehouse || warehouse === "all" || String(p.sklad) === String(warehouse);
    const local = products.filter((p) => {
      if (!inSklad(p)) return false;
      if (String(p?.seriyasi || "").trim().toLowerCase() === needle) return true;
      // qr_kod bitta mahsulotda bir nechta barkodni bo'sh joy/vergul bilan saqlashi mumkin
      const codes = String(p?.qr_kod || "")
        .toLowerCase()
        .split(/[\s,]+/)
        .filter(Boolean);
      return codes.includes(needle);
    });

    // Ro'yxat sahifalangan — boshqa mosliklar hali yuklanmagan bo'lishi mumkin, serverdan ham so'raymiz
    let matched = local;
    try {
      // code= — seriya/qr_kod bo'yicha aniq moslik (search kabi qisman emas)
      const scanParams = new URLSearchParams({ view: "sale", code: series, page_size: "20" });
      if (warehouse && warehouse !== "all") scanParams.set("sklad", String(warehouse));
      const rows = asArr(await api.get(`maxsulot/?${scanParams.toString()}`)).filter(inSklad);
      matched = uniqById([...rows, ...local]);
      if (rows.length) setProducts((prev) => uniqById([...rows, ...prev]));
    } catch (e) {
      console.log("Scan search error:", e?.response?.status, e?.response?.data);
    }

    if (!matched.length) {
      const skladMsg = warehouse && warehouse !== "all" ? " (tanlangan skladda)" : "";
      alert(`Mahsulot topilmadi${skladMsg}: ${series}`);
      return;
    }
    if (matched.length > 1) {
      setScanChoices({ code: series, items: matched });
      return;
    }
    addScanned(matched[0]);
  }, [products, addScanned, warehouse]);

  useEffect(() => {
    const SCAN_GAP_MS = 80;
    const SCAN_TOTAL_MS = 400;
    const MIN_SCAN_LEN = 4;

    const scan = {
      buf: "", lastTs: 0, firstTs: 0,
      active: false, target: null, targetValue: "",
    };

    const isEditable = (el) => {
      if (!el) return false;
      const tag = String(el.tagName || "").toUpperCase();
      return el.isContentEditable || tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    };

    const reset = () => {
      scan.buf = ""; scan.lastTs = 0; scan.firstTs = 0;
      scan.active = false; scan.target = null; scan.targetValue = "";
    };

    const restoreTarget = () => {
      const el = scan.target;
      if (!el || !isEditable(el)) return;
      try {
        const prev = scan.targetValue ?? "";
        if (el.value !== prev) {
          el.value = prev;
          el.dispatchEvent(new Event("input", { bubbles: true }));
          el.dispatchEvent(new Event("change", { bubbles: true }));
        }
      } catch {}
    };

    const onKeyDown = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const now = Date.now();
      const gap = now - scan.lastTs;
      if (gap > SCAN_GAP_MS) reset();
      scan.lastTs = now;

      if (e.key === "Enter") {
        const buf = scan.buf.trim();
        const isScan =
          scan.active && buf.length >= MIN_SCAN_LEN &&
          scan.firstTs > 0 && now - scan.firstTs <= SCAN_TOTAL_MS;
        if (isScan && isEditable(e.target)) e.preventDefault();
        if (isScan) restoreTarget();
        reset();
        if (isScan) handleScanSeries(buf);
        return;
      }

      if (e.key && e.key.length === 1) {
        if (!scan.buf) {
          scan.firstTs = now;
          if (isEditable(e.target)) {
            scan.target = e.target;
            scan.targetValue = e.target.value ?? "";
          }
        } else if (scan.firstTs && now - scan.firstTs > SCAN_TOTAL_MS) {
          reset();
          scan.firstTs = now;
          if (isEditable(e.target)) {
            scan.target = e.target;
            scan.targetValue = e.target.value ?? "";
          }
        }
        scan.buf += e.key;
        if (!scan.active && scan.buf.length >= MIN_SCAN_LEN) scan.active = true;
        if (scan.active && isEditable(e.target)) e.preventDefault();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleScanSeries]);

  // useCallback: CartRow memo'lari har renderda qayta chizilmasligi uchun
  const setQtyAbs = useCallback((id, newQty) =>
    setCart((old) => {
      const maxStk = stockByProductId(products, id, old);
      const clamped = clampQtyByStock(newQty, maxStk);
      if (maxStk != null && newQty > maxStk) {
        const line = old.find((x) => x.id === id);
        alert(
          `${line?.name || "Mahsulot"} (${line?.sku || ""}) uchun omborda ${maxStk} dona bor.`
        );
      }
      const upd = old.map((it) => (it.id === id ? { ...it, qty: clamped } : it));
      const found = upd.find((x) => x.id === id);
      return found ? [found, ...upd.filter((x) => x.id !== id)] : upd;
    }), [products]);


  const removeItem = useCallback((id) => setCart((old) => old.filter((it) => it.id !== id)), []);

  const changeUnit = useCallback((id, newUnit) =>
    setCart((old) => {
      const maxStk = stockByProductId(products, id, old);
      const upd = old.map((it) => {
        if (it.id !== id) return it;
        const qty = clampQtyByStock(it.qty, maxStk);
        return { ...it, unitPrice: Math.max(0, toNum(newUnit)), qty };
      });
      return upd;
    }), [products]);


  const changeLineTotal = useCallback((id, newTotal) =>
    setCart((old) => {
      const maxStk = stockByProductId(products, id, old);
      const upd = old.map((it) => {
        if (it.id !== id) return it;
        const qty = clampQtyByStock(it.qty, maxStk);
        const unit = Math.max(0, toNum(newTotal) / Math.max(1, qty));
        return { ...it, unitPrice: unit, qty };
      });
      return upd;
    }), [products]);



/* ------- UI helpers ------- */
const labelFromPriceTypeId = (id, list) =>
  (list.find(t => String(t.id) === String(id))?.nom || "").toLowerCase();

const resolvePriceLabel = (seller, selectedPriceTypeId, priceTypes) => {
  const has = (label) =>
    priceTypes.some(
      (pt) => String(pt.nom || pt.name || "").toLowerCase() === String(label).toLowerCase()
    );

  // mijoz tarifi ("vip", "optom", "donaga", ...)
  const sLab = String(seller?.tariff || seller?.mijoztarifi || "").toLowerCase();
  if (sLab && has(sLab)) return sLab;

  // selectdan tanlangan narx turi (id -> nom)
  const fromSelect = labelFromPriceTypeId(selectedPriceTypeId, priceTypes);
  if (fromSelect && has(fromSelect)) return fromSelect;

  // default
  return "donaga";
};

  const openCalc = () => {
    const exp = prompt("Hisob-kitob kiriting (masalan: 12.5*3 + 7/2)");
    if (exp == null) return;
    if (!/^[\d.+\-*/()%\s]+$/.test(exp)) { alert("Faqat raqam va + - * / ( ) % belgilariga ruxsat."); return; }
    try { /* eslint-disable no-new-func */
      const result = Function(`"use strict"; return (${exp})`)();
      alert(`Natija: ${result}`);
    } catch { alert("Ifoda xato."); }
  };

  const cartFiltered = useMemo(() => {
    const q = qCart.trim().toLowerCase();
    if (!q) return cart;
    return cart.filter((c) => `${c.name} ${c.sku}`.toLowerCase().includes(q));
  }, [cart, qCart]);

  const subtotal = cart.reduce((s, it) => s + toNum(it.unitPrice) * it.qty, 0);
  const disc = computeDiscountValue(discountMode, discount, subtotal);
  const totalDue = Math.max(0, subtotal - disc);

  /* ------- Paid / Remaining / Change ------- */
  // POS kabi: naqd tanlangan va summa yozilmagan bo'lsa — to'liq summa naqd olindi deb hisoblanadi
  const noPayInput = !toSomInt(inUsd) && !toSomInt(inUzsCash) && !toSomInt(inUzsCard);
  const exactCash = payType === "naxt" && noPayInput;
  const effCashSom = exactCash ? Math.round(totalDue) : toSomInt(inUzsCash);
  const paidUsdEq =
     toNum(inUsd)
     + effCashSom
     + toNum(inUzsCard);
  const remainingUsd = Math.max(0, totalDue - paidUsdEq);
  const changeUsd = Math.max(0, paidUsdEq - totalDue);
  const remainingUzs = Math.round(remainingUsd);
  const changeUzs = Math.round(changeUsd);

  /* ------- Tasdiqlash holati ------- */
  const EPS = 1e-9;
  const hasChange = payType === "naxt" && changeUsd > EPS;
  const canConfirm =
    !!seller &&
    cart.length > 0 &&
    (
      (payType === "naxt"   && (paidUsdEq + EPS) >= totalDue) ||
      (payType === "nasiya" && toNum(termDays) > 0)
    );

  const buildChangeReason = ({ changeUsd: usd, changeUzs: uzs, note: saleNote }) => {
    const parts = [`Savdo qaytimi ${toSomRounded(usd)} so'm`];
    if (uzs > 0) parts.push(`${toSomRounded(uzs)} so'm`);
    const cleanNote = String(saleNote || "").trim();
    if (cleanNote) parts.push(cleanNote);
    return parts.join(" | ").slice(0, 240);
  };
  const postChangeToSellerBalance = async ({ seller, changeUsd }) => {
    const sId = seller?.id;
    const amt = toNum(changeUsd);  // qaytim USD ekvivalenti
    if (!sId || amt <= 0) return { ok: false };
    
    try {
      // 1) Hozirgi balansni olib kelamiz
      const cur = await api.get(`sotuvchi/${sId}/`);
      const curSigned = cur?.data?.qarhaq ?? 0;
    
      // 2) Yangi balans (qarhaq + qaytim)
      const newSigned = toFixed3Str(toNum(curSigned) + amt);
    
      // 3) PATCH orqali yangilaymiz
      const res = await api.patch(`sotuvchi/${sId}/`, {
        qarhaq: newSigned,
        balance_reason: buildChangeReason({
          changeUsd: amt,
          changeUzs,
          note,
        }),
        // izoh maydoni yo‘q, shuning uchun yubormasak ham bo‘ladi
      });
    
      return { ok: true, sellerData: res?.data || null };
    } catch (e) {
      console.log(
        "postChangeToSellerBalance ERROR",
        e?.response?.status,
        e?.response?.data || e.message
      );
      return { ok: false, error: e };
    }
  };

  const ensureCashChangeAvailable = async (somAmount) => {
    if (somAmount <= 0) return { ok: true, availableAfterIncome: 0 };

    const res = await api.get("kassa/");
    const currentCash = toSomRounded(res?.data?.cash_som ?? 0);
    const currentSaleCash = toSomInt(inUzsCash);
    const availableAfterIncome = currentCash + currentSaleCash;

    return {
      ok: availableAfterIncome >= somAmount,
      currentCash,
      availableAfterIncome,
    };
  };

  const postCashChangeWithdraw = async ({ seller, changeUsd, changeUzs }) => {
    const somAmount = toSomRounded(changeUzs);
    if (somAmount <= 0) return { ok: true };

    try {
      await api.post("kassa/withdraw/", {
        nom: seller?.name || "Savdo qaytimi",
        reason: buildChangeReason({
          changeUsd,
          changeUzs: somAmount,
          note,
        }),
        cash_som: somAmount,
        card_som: 0,
        terminal_som: 0,
      });
      return { ok: true };
    } catch (e) {
      console.log(
        "postCashChangeWithdraw ERROR",
        e?.response?.status,
        e?.response?.data || e.message
      );
      return { ok: false, error: e };
    }
  };



  /* ------- Tasdiqlash flow ------- */
  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      if (!seller) { alert("Avval haridorni tanlang."); return; }
    
      const bad = cart.find((c) => !c?.sklad);
      if (bad) {
        alert("Xato: savatchadagi ayrim mahsulotlarda sklad aniqlanmagan. Mahsulotni qayta tanlang.");
        return;
      }
    
      const isNasiya = payType === "nasiya";
      if (!(cart.length > 0 && (payType === "naxt" || (isNasiya && toNum(termDays) > 0)))) {
        alert("To‘lov turini tanlang va (agar nasiya bo‘lsa) muddat kiriting.");
        return;
      }
    
      // yakuniy stok tekshiruvi
      const badLine = cart.find((c) => {
        const stk = stockByProductId(products, c.id, cart);
        return stk != null && c.qty > stk;
      });
      if (badLine) {
        const stk = stockByProductId(products, badLine.id, cart) ?? 0;
        alert(`${badLine.name || "Mahsulot"} (${badLine.sku || ""}) yetarli emas. Omborda: ${stk} dona. Miqdorni kamaytiring.`);
        return;
      }

      if (hasChange && changeMode === "cash") {
        const cashCheck = await ensureCashChangeAvailable(changeUzs);
        if (!cashCheck.ok) {
          alert(
            `Kassada qaytim uchun yetarli so'm yo'q.\nKerak: ${fmtUZS(changeUzs)}\nMavjud (shu savdo naqd kirimi bilan): ${fmtUZS(cashCheck.availableAfterIncome)}`
          );
          return;
        }
      }
    
      // === sizdagi hozirgi try ichidagi butun flow shu yerda davom etadi ===
      const tolovTuri = payType === "naxt" ? "naqd" : "nasiya";
    
      let nmuddat = null;
      if (tolovTuri === "nasiya") {
        const days = Math.max(1, Math.floor(toNum(termDays)));
        const d = new Date();
        d.setDate(d.getDate() + days);
        nmuddat = toLocalYMD(d);
      }
    
      const clientOrderId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      const receiptPayload = buildReceiptPayload({
        cart, seller, payType: tolovTuri, discountValue: disc, products, note,
      });

      // Savdo qatorlari + chek + kassa kirimi bitta so'rovda, bitta tranzaksiyada
      const terminalSom = toSomInt(inUsd);
      const cashSom = effCashSom;
      const cardSom = toSomInt(inUzsCard);
      const hasIncome = payType === "naxt" && (terminalSom > 0 || cashSom > 0 || cardSom > 0);

      const { data: checkout } = await api.post("sotilgan-maxsulot/checkout/", {
        lines: cart.map((c) => ({
          sotuvchi: seller.id,
          maxsulot: c.id,
          sklad: c?.sklad ?? null,
          sotilgansoni: c.qty,
          narx: toFixed3Str(c.unitPrice),
          tolov_turi: tolovTuri,
          nmuddat,
          izoh: note || "",
          client_order_id: clientOrderId,
          client_line_id: String(c.id),
        })),
        receipt: receiptPayload,
        kassa_income: hasIncome
          ? {
              buyer_name: seller?.name || "",
              terminal_som: terminalSom,
              cash_som: cashSom,
              card_som: cardSom,
              note: (note || "").trim(),
            }
          : null,
      });

      const receipt = checkout?.receipt || {
        ...receiptPayload,
        subtotal: toFixed3Str(subtotal),
        discount: toFixed3Str(disc),
        total: toFixed3Str(totalDue),
        created_at: new Date().toISOString(),
      };

      // Sotilgan miqdorni ekrandagi qoldiqdan ayiramiz (qayta yuklamasdan)
      const soldQty = new Map(cart.map((c) => [c.id, c.qty]));
      setProducts((prev) =>
        prev.map((p) => (soldQty.has(p.id) ? { ...p, soni: stockOf(p) - soldQty.get(p.id) } : p))
      );

      setLastReceipt({
        receipt,
        extras: getReceiptPrintExtras(receipt, {
          payType: tolovTuri,
          dueDate: nmuddat,
          inTerminalSom: inUsd,
          inCashSom: effCashSom,
          inCardSom: inUzsCard,
          note,
        }),
      });
      const resultLines = ["Savdo, chek va kassa kirimi saqlandi.", "Chek kerak bo'lsa — \"Chek chiqarish\" tugmasini bosing."];
      if (payType === "naxt" && remainingUsd > 0) {
        resultLines.push(`Diqqat: ${fmtUZS(remainingUzs)} hali to'lanmagan.`);
      }

      if (hasChange) {
        if (changeMode === "balance") {
          const changeResult = await postChangeToSellerBalance({ seller, changeUsd });
          if (changeResult.ok) {
            if (changeResult.sellerData) {
              setSeller(mapSellerRecord({ ...seller, ...changeResult.sellerData }));
            }
            resultLines.push(`Qaytim balansga yozildi: ${fmtUZS(changeUzs)}.`);
          } else {
            resultLines.push("Diqqat: qaytimni balansga yozib bo'lmadi. Balansni tekshiring.");
          }
        } else {
          const changeResult = await postCashChangeWithdraw({ seller, changeUsd, changeUzs });
          if (changeResult.ok) {
            resultLines.push(`Qaytim kassadan qaytarildi: ${fmtUZS(changeUzs)}.`);
          } else {
            resultLines.push("Diqqat: qaytim kassadan yechilmadi. Kassa tarixini tekshiring.");
          }
        }
      }

      alert(resultLines.join("\n"));
      setCart([]); setPayType("naxt"); setTermDays(""); setDiscount("0"); setNote("");
      setInUsd(""); setInUzsCash(""); setInUzsCard(""); setChangeMode("cash");
    
    } catch (e) {
      console.log("CONFIRM ERR", e?.response?.status, e?.response?.data);
      alert(`Saqlashda xato: ${e?.response?.status || ""} ${JSON.stringify(e?.response?.data || e.message)}`);
    } finally {
      setIsSubmitting(false);
    }
  };


  // grid uchun filterlangan productlar
  const gridProducts = useMemo(() => {
    let list = products;

    if (stockFilter === "available") {
      // faqat qolgan maxsulotlar (stok > 0)
      list = list.filter((p) => stockOf(p) > 0);
    } else if (stockFilter === "out") {
      // faqat tugagan maxsulotlar (stok <= 0)
      list = list.filter((p) => stockOf(p) <= 0);
    }
    // "all" bo‘lsa hech narsa qilmaymiz

    return list;
  }, [products, stockFilter]);

  const visibleProducts = useMemo(
    () => gridProducts.slice(0, renderLimit),
    [gridProducts, renderLimit]
  );

  // Har kartochka uchun qayta hisoblanmasin: narx turi va savatdagi id'lar bir marta
  const activePriceLabel = useMemo(
    () => resolvePriceLabel(seller, priceType, priceTypes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seller, priceType, priceTypes]
  );
  const cartIds = useMemo(() => new Set(cart.map((c) => c.id)), [cart]);


  /* ======= UI ======= */
  return (
    <div className={`sale-wrap ${mobileTab === "checkout" ? "tab-checkout" : "tab-products"}`}>
      {/* ===== Title bar ===== */}
      <div className="title-line">
        <div className="title-head">
          <h2>Savdo amalga oshirish bo‘limi</h2>

          <button className="btn btn-ghost" onClick={openCalc}>Kalkulyator</button>
          <Link className="btn btn-ghost" to={adminPath("CheklarTarixi")}>Cheklar tarixi</Link>

          {seller && (
            <div className="mini-cust chip inline">
              <img src={customerImg(seller)} alt="" />
              <div className="mini-lines">
                <b className="mini-name">{seller.name}</b>
                <span className="mini-phone">{seller.phone || "—"}</span>
              </div>
              <span className={`debt-badge ${badgeClass(seller.uiState)}`}>
                {seller.uiState === UI_STATES.OVERDUE ? "Muddati o‘tgan: " :
                 seller.uiState === UI_STATES.NOT_DUE ? "Qarzdor: " :
                 seller.uiState === UI_STATES.CREDITOR ? "Haqdor: " : "Neytral: "}
                <b>{fmtUSD(seller.debtAbs)}</b>
              </span>
              <button
                type="button"
                className={`pin-btn ${pinnedIds.has(seller.id) ? "on" : ""}`}
                title={pinnedIds.has(seller.id) ? "Qadalganlardan olib tashlash" : "Xaridorni qadab qo'yish"}
                onClick={() => togglePin(seller)}
              >
                {pinnedIds.has(seller.id) ? "★" : "☆"}
              </button>
            </div>
          )}

          {/* Qadalgan xaridorlar — bir bosishda tanlash */}
          {pinned.length > 0 && (
            <div className="pinned-sellers">
              {pinned.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`pinned-chip ${seller?.id === p.id ? "on" : ""} ${badgeClass(p.uiState)}`}
                  onClick={() => selectPinned(p)}
                  title={`${p.name}${p.phone ? " · " + p.phone : ""}`}
                >
                  ★ {p.name}
                </button>
              ))}
            </div>
          )}

          {/* o'ng chekkadagi seller qidiruv inputi */}
          <div className="title-spacer" />
          <div className="title-right">
            <div className="seller-search">
              <input
                className="input"
                placeholder="Haridorni qidiring"
                value={qSellers}
                onChange={(e) => setQSellers(e.target.value)}
              />
              {sellerMatches.length > 0 && (
                <div className="seller-dd">
                  {sellerMatches.map((s) => (
                    <div
                      key={s.id}
                      className="seller-dd-item"
                      onClick={() => { setSeller(s); setQSellers(""); }}
                    >
                      <img src={s.avatar || "https://i.pravatar.cc/80?img=1"} alt="" />
                      <div className="dd-text">
                        <b>{s.name}</b><span>{s.phone || "—"}</span>
                      </div>
                      <span className={`dd-badge ${
                        s.uiState === UI_STATES.CREDITOR ? "green" :
                        s.uiState === UI_STATES.NOT_DUE ? "yellow" :
                        s.uiState === UI_STATES.OVERDUE ? "red" : "gray"
                      }`}>
                        {fmtUSD(s.debtAbs)}
                      </span>
                      <button
                        type="button"
                        className={`pin-btn ${pinnedIds.has(s.id) ? "on" : ""}`}
                        title={pinnedIds.has(s.id) ? "Qadalganlardan olib tashlash" : "Qadab qo'yish"}
                        onClick={(e) => { e.stopPropagation(); togglePin(s); }}
                      >
                        {pinnedIds.has(s.id) ? "★" : "☆"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==== Filtrlar + mahsulot qidirish ==== */}
      <div className="filters">
        <select value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>{w.nom || w.name}</option>
          ))}
        </select>

        {/* narx turi */}
        <select value={priceType} onChange={(e) => setPriceType(e.target.value)}>
          {priceTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.nom || t.name}</option>
          ))}
        </select>


        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.nom || c.name}</option>
          ))}
        </select>

        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>{b.nom || b.name}</option>
          ))}
        </select>

        <input
          className="input search-in"
          placeholder="Mahsulot nomi, seriyasi bilan qidiring"
          value={qProductsLeft}
          onChange={(e) => setQProductsLeft(e.target.value)}
        />


        {/* 🔥 YANGI: Qolgan/qolmagan/barchasi filtri */}
        <select
          className="stock-filter"
          value={stockFilter}
          onChange={(e) => setStockFilter(e.target.value)}
        >
          <option value="available">Qolgan</option>
          <option value="out">Qolmagan</option>
          <option value="all">Barchasi</option>
        </select>

        {/* Chegirmadan keyingi yakuniy summa — katta, kassir va mijoz ko'rsin */}
        <div className="top-total" title="Chegirmadan keyingi yakuniy summa">
          <div className="top-total-sub">
            <span>Jami {fmtUZS(subtotal)}</span>
            <span>Chegirma {disc > 0 ? "−" : ""}{fmtUZS(disc)}</span>
          </div>
          <div className="top-total-sum">{fmtUZS(totalDue)}</div>
        </div>
      </div>

      {/* GRID */}
      <div
        className="sale-grid"
        ref={gridRef}
        style={cartW ? { "--cart-w": `${cartW}px` } : undefined}
      >
        {/* LEFT */}
        <div className="left">
          <div className="left-box">
            <div className="p-scroll">
              <div className="p-grid">
                {visibleProducts.map((p) => {
                  const outOfStock = stockOf(p) <= 0;
                  // Sklad tanlangan bo'lsa va mahsulot boshqa sklad'dan bo'lsa — sotish mumkin emas
                  const wrongSklad = warehouse && warehouse !== "all"
                    && p?.sklad && String(p.sklad) !== String(warehouse);
                  const allowSell = p?.can_sell !== false && !wrongSklad;

                  // mijoz tarifiga yoki selectdagi narx turiga qarab narx
                  let usd = priceByTypeUSD(p, activePriceLabel, priceTypes);
                  if (usd == null && activePriceLabel !== "donaga") {
                    usd = priceByTypeUSD(p, "donaga", priceTypes);
                  }

                  return (
                    <ProductCard
                      key={p.id}
                      product={p}
                      usdPrice={usd}
                      outOfStock={outOfStock}
                      allowSell={allowSell}
                      wrongSklad={wrongSklad}
                      selected={cartIds.has(p.id)}
                      onAdd={addToCart}
                    />
                  );
                })}

              </div>
              {gridProducts.length === 0 && !loadingPage && <div className="empty">Mahsulot topilmadi</div>}
              {loadingPage && <div className="empty small">Yuklanmoqda…</div>}
              <div ref={loaderRef} style={{ height: 1 }} />
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="right">
          <div
            className="cart-resizer"
            onPointerDown={startCartResize}
            onDoubleClick={() => { gridRef.current?.style.removeProperty("--cart-w"); saveCartW(null); }}
            title="Sudrab kattalashtiring / kichiklashtiring. Ikki marta bosish — asl o'lcham"
          />
          <div className="right-inner">
            <input
              className="input input-tight"
              placeholder="Tanlangan mahsulotlar ichidan qidiring"
              value={qCart}
              onChange={(e) => setQCart(e.target.value)}
            />

            {/* CART */}
            <div className="cart-list">
              {cartFiltered.map((it) => (
                <CartRow
                  key={it.id}
                  item={it}
                  onQtyAbs={setQtyAbs}
                  onRemove={removeItem}
                  onUnitChange={changeUnit}
                  onLineChange={changeLineTotal}
                />
              ))}
              {cartFiltered.length === 0 && <div className="empty small">Ro‘yxat bo‘sh</div>}
            </div>

            {/* To‘lov turi + muddat */}
            <div className="pay-box">
              <div className="pay-seg">
                <button className={`seg-btn ${payType === "naxt" ? "on" : ""}`} onClick={() => setPayType("naxt")} type="button">Naqt</button>
                <button className={`seg-btn ${payType === "nasiya" ? "on" : ""}`} onClick={() => setPayType("nasiya")} type="button">Nasiya</button>

                <input
                  className="input input-kun inline"
                  type="number"
                  min="1"
                  placeholder="Muddat (kun)"
                  value={termDays}
                  onChange={(e) => setTermDays(e.target.value)}
                  disabled={payType !== "nasiya"}
                />
              </div>

              {/* Chegirma */}
              <div className="discount-wrap">
                <div className="discount-mode">
                  <label>
                    <input type="radio" name="dm" checked={discountMode === "amount"} onChange={() => setDiscountMode("amount")} /> Miqdor
                  </label>
                </div>
                <div className="discount-mode" style={{marginLeft:12}}>
                  <label>
                    <input type="radio" name="dm" checked={discountMode === "percent"} onChange={() => setDiscountMode("percent")} /> %
                  </label>
                </div>
                <div className="discount-input">
                  <label>Chegirma:</label>
                  <input
                    className="input input-kun"
                    type="text"
                    inputMode="decimal"
                    value={discount}
                    onChange={(e) => setDiscount(normDecStr(e.target.value))}
                    onBlur={(e) => setDiscount(normDecStr(e.target.value))}
                  />
                  <span className="muted">{discountMode === "amount" ? "so'm" : "%"}</span>
                </div>
              </div>

              {/* Totallar */}
              <div className="totals-inline">
                <div>Jami: <b>{fmtUZS(subtotal)}</b></div>
                <div>Chegirma: <b>{fmtUZS(disc)}</b></div>
                <div className="grand">Yakuniy: <b>{fmtUZS(totalDue)}</b></div>
              </div>

              {/* Naqt rejimida – qolgan/qaytim indikatorlari */}
              {payType === "naxt" && totalDue > 0 && (
                <div className="pay-hints">
                  {remainingUsd > 0 && (
                    <div className="hint need">
                      Qolgan to‘lov: <b>{fmtUZS(remainingUzs)}</b>
                    </div>
                  )}
                  {hasChange && (
                    <div className="hint change">
                      <div>Qaytim: <b>{fmtUZS(changeUzs)}</b></div>
                      <div className="change-settle">
                        <span className="change-settle-label">Qaytimni:</span>
                        <div className="mini-seg">
                          <button
                            type="button"
                            className={changeMode === "cash" ? "on" : ""}
                            onClick={() => setChangeMode("cash")}
                          >
                            Kassadan qaytarish
                          </button>
                          <button
                            type="button"
                            className={changeMode === "balance" ? "on" : ""}
                            onClick={() => setChangeMode("balance")}
                          >
                            Balansga yozish
                          </button>
                        </div>
                      </div>
                      <div className="change-settle-note">
                        {changeMode === "cash"
                          ? `Kassadan ${fmtUZS(changeUzs)} chiqim qilinadi.`
                          : `Mijoz balansiga ${fmtUZS(changeUzs)} yoziladi.`}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* KASSA breakdown — faqat NAQT rejimida */}
              {payType === "naxt" && (
                <div className="cash-in">
                  {/* Bitta bosishda to'liq summa — summani qo'lda yozish shart emas */}
                  <div className="quick-pay">
                    {[
                      ["cash", "To‘liq naqd"],
                      ["card", "To‘liq karta"],
                      ["terminal", "To‘liq terminal"],
                    ].map(([k, label]) => {
                      const full = String(Math.round(totalDue));
                      const on =
                        (k === "cash" && (exactCash || (inUzsCash === full && !toSomInt(inUsd) && !toSomInt(inUzsCard)))) ||
                        (k === "card" && inUzsCard === full && !toSomInt(inUsd) && !toSomInt(inUzsCash)) ||
                        (k === "terminal" && inUsd === full && !toSomInt(inUzsCash) && !toSomInt(inUzsCard));
                      return (
                        <button
                          key={k}
                          type="button"
                          className={`quick-pay-btn ${on ? "on" : ""}`}
                          disabled={!totalDue}
                          onClick={() => {
                            setInUzsCash(k === "cash" ? full : "");
                            setInUzsCard(k === "card" ? full : "");
                            setInUsd(k === "terminal" ? full : "");
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {exactCash && totalDue > 0 && (
                    <div className="muted quick-pay-note">
                      Summa yozilmasa — {fmtUZS(totalDue)} naqd olindi deb hisoblanadi.
                    </div>
                  )}
                  {/* Tartib tez tugmalar bilan bir xil: Naqd · Karta · Terminal */}
                  <div className="line">
                    <label>Naqd so'm</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      placeholder={exactCash && totalDue > 0 ? String(Math.round(totalDue)) : "0"}
                      value={inUzsCash}
                      onChange={(e) => setInUzsCash(String(toSomInt(e.target.value)))}
                    />
                  </div>
                  <div className="line">
                    <label>Karta so'm</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      placeholder="0"
                      value={inUzsCard}
                      onChange={(e) => setInUzsCard(String(toSomInt(e.target.value)))}
                    />
                  </div>
                  <div className="line">
                    <label>Terminal so'm</label>
                    <input
                      className="input"
                      type="text"
                      inputMode="numeric"
                      placeholder="0"
                      value={inUsd}
                      onChange={(e) => setInUsd(String(toSomInt(e.target.value)))}
                    />
                  </div>
                </div>
              )}



              <div className="note-box">
                <label>Izoh:</label>
                <textarea
                  className="input note-textarea"
                  placeholder={payType === "nasiya" ? "Nasiya shartlari, kelishuv, eslatma..." : "Ixtiyoriy izoh..."}
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              <div className="actions">
                <button className="btn btn-ghost" onClick={() => setCart([])}>Tozalash</button>
                <button
                  className="btn btn-primary"
                  disabled={!canConfirm || isSubmitting}
                  onClick={handleConfirm}
                >
                  {isSubmitting ? "Yuborilmoqda..." : "Tasdiqlash"}
                </button>
                <button
                  className="btn btn-ghost"
                  disabled={!lastReceipt || isSubmitting}
                  title={lastReceipt ? "Oxirgi tasdiqlangan savdo chekini chiqarish" : "Avval savdoni tasdiqlang"}
                  onClick={() => lastReceipt && openSharedReceiptWindow(lastReceipt.receipt, lastReceipt.extras)}
                >
                  🧾 Chek chiqarish
                </button>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bir xil shtrix-kodli mahsulotlardan tanlash */}
      {scanChoices && (
        <div className="scan-pick-bg" onClick={() => setScanChoices(null)}>
          <div className="scan-pick" onClick={(e) => e.stopPropagation()}>
            <div className="scan-pick-head">
              <div>
                <b>Qaysi mahsulot?</b>
                <div className="scan-pick-code">Kod: {scanChoices.code} — {scanChoices.items.length} ta mahsulot</div>
              </div>
              <button type="button" className="btn btn-ghost" onClick={() => setScanChoices(null)}>✕</button>
            </div>
            <div className="scan-pick-list">
              {scanChoices.items.map((p) => {
                const out = stockOf(p) <= 0 || p?.can_sell === false;
                let price = priceByTypeUSD(p, activePriceLabel, priceTypes);
                if (price == null) price = priceByTypeUSD(p, "donaga", priceTypes);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className="scan-pick-item"
                    disabled={out}
                    onClick={() => { addScanned(p); setScanChoices(null); }}
                  >
                    <span className="scan-pick-name">{p.nom}</span>
                    <span className="scan-pick-meta">{p.seriyasi} · {out ? "Stok yo‘q" : `${stockOf(p)} dona`}</span>
                    <b className="scan-pick-price">{price != null ? fmtUZS(price) : "—"}</b>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom switcher */}
      <div className="mobile-bar">
        <button
          type="button"
          className={`mb-btn ${mobileTab === "products" ? "on" : ""}`}
          onClick={() => setMobileTab("products")}
        >
          Mahsulotlar
        </button>
        <button
          type="button"
          className={`mb-btn ${mobileTab === "checkout" ? "on" : ""}`}
          onClick={() => setMobileTab("checkout")}
        >
          Haridor &amp; Savdo
        </button>
      </div>
    </div>
  );
}
